import { useMemo } from 'react';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useBrandingPreview } from '../../hooks/useTeams';
import type { TeamBrandingPreviewParams } from '../../lib/api/teams';

/** Shares the preview's query/cache; never derives a competing client verdict. */
export function BrandingLogoWarning({ teamId, accent, background, hidePoweredBy, logoUrl }: TeamBrandingPreviewParams & { teamId: string | undefined }) {
  const live = useMemo(() => ({ accent, background, hidePoweredBy, logoUrl }), [accent, background, hidePoweredBy, logoUrl]);
  const params = useDebouncedValue(live, 400);
  const preview = useBrandingPreview(teamId, params, !!live.logoUrl);
  if (!live.logoUrl) return null;
  const checking = preview.isFetching || preview.isLoading ||
    params.logoUrl !== live.logoUrl || params.background !== live.background ||
    params.accent !== live.accent || params.hidePoweredBy !== live.hidePoweredBy;
  if (checking) return <p role="status" className="mt-2 text-xs text-gray-500">Checking logo readability...</p>;
  const verdict = preview.data?.logo;
  if (verdict?.status === 'checked' && verdict.lowContrastSurfaces.length === 0) return null;

  let message: string;
  if (!verdict || verdict.status === 'unavailable') {
    message = 'We couldn’t check this logo’s contrast. Check it in the preview and on your shared pages.';
  } else if (verdict.status === 'empty') {
    message = 'This image appears to be transparent. Try a logo with visible artwork.';
  } else {
    const review = verdict.lowContrastSurfaces.includes('review');
    const shared = verdict.lowContrastSurfaces.some((surface) => surface !== 'review');
    message = review && shared
      ? 'Your logo may be hard to see on the review header and light shared pages. Try a logo with its own contrasting background.'
      : review
        ? 'Your logo may be hard to see on this header colour. Choose a different logo or header colour.'
        : 'Your logo may be hard to see on the review portal and shared issue pages. These pages use light backgrounds. Try a darker logo or one with its own contrasting background.';
  }
  return <div role="alert" data-testid="branding-logo-warning" className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
    <p className="font-medium">Check logo readability</p>
    <p className="mt-1">{message}</p>
  </div>;
}
